'use client';

import { useCallback, useEffect, useRef } from 'react';

/**
 * Gère l'apparition au scroll des éléments marqués `.reveal`.
 *
 * - `addRevealRef` : à passer en `ref` sur chaque élément à révéler.
 * - `resetRefs`    : à appeler avant un changement de contenu (ex. changement d'univers)
 *                    pour repartir d'une collection propre.
 *
 * L'observer est ré-instancié à chaque changement de `resetKey`.
 */
export function useScrollReveal(resetKey: number) {
  const revealRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [resetKey]);

  const addRevealRef = useCallback((el: HTMLElement | null) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  }, []);

  const resetRefs = useCallback(() => {
    revealRefs.current = [];
  }, []);

  return { addRevealRef, resetRefs };
}

export type AddRevealRef = (el: HTMLElement | null) => void;
